import { DatabaseFieldType } from "classes/database/database_field_type.enum";
import { DBFactory } from "classes/database/db_factory";
import { Observable, map } from "rxjs";
import { CapabilitiesService } from "../capabilities/capabilities.service";
import { CapabilityPackage } from "../capabilities/capability-package.model";
import { SubscriptionInfo } from "../capabilities/subscription-info.model";
import { UserCapabilities } from "../capabilities/user-capabilities.model";
import { UserPackage, UserPackages } from "../capabilities/user-package.model";
import { ClassroomsService } from "../classrooms/classrooms.service";
import { LMSOrganizationConfig } from "./lms-organization-config.model";
import {
  OrganizationPackage,
  OrganizationPackages,
} from "./organization-package.model";
import { OrganizationSummary } from "./organization-summary.model";
import { Organization, Organizations } from "./organization.model";
import type {
  SubscriptionOwnershipDecision,
  SubscriptionOwnershipTransferPreview,
} from "~/types/subscription-ownership-transfer";

export class OrganizationsService {
  static async listOrganizations(): Promise<Organizations> {
    const db = DBFactory.createDatabase();
    const data = await db.list({
      path: "/organizations",
      collection: "/organizations",
    });
    return data.map((organization: any) => Organization.fromMap(organization));
  }

  static streamOrganizations(): Observable<Organizations> {
    const db = DBFactory.createDatabase();
    return db
      .streamList({
        path: "/organizations",
        collection: "/organizations",
      })
      .pipe(
        map((data: any) => {
          return data.map((org: any) => {
            return Organization.fromMap(org);
          });
        })
      );
  }

  static async fetchUserOrganizations({
    organizationIds,
  }: {
    organizationIds: string[];
  }) {
    const db = DBFactory.createDatabase();
    const data = await db.list(
      {
        path: "/organizations",
        collection: "/organizations",
      },
      [
        {
          type: "where",
          field: "id",
          operator: "in",
          fieldType: DatabaseFieldType.id,
          value: organizationIds,
        },
      ]
    );

    return data.map((org: any) => {
      return Organization.fromMap(org);
    });
  }

  static async get(id: string): Promise<Organization> {
    try {
      const db = DBFactory.createDatabase();
      const data = await db.get({
        path: `/organizations/${id}`,
        collection: `/organizations`,
      });

      if (!data) {
        throw new Error("Organization not found");
      }

      return Organization.fromMap(data);
    } catch (error) {
      throw new Error("Organization not found");
    }
  }

  static async getOrganizationLMSConfig(
    organizationId: string
  ): Promise<LMSOrganizationConfig | undefined> {
    try {
      const db = DBFactory.createDatabase();
      const data = await db.get({
        path: `/lmsOrganizationConfigs/${organizationId}`,
        collection: `/lmsOrganizationConfigs`,
      });

      return LMSOrganizationConfig.fromMap(data);
    } catch (error) {
      return undefined;
    }
  }

  static streamOrganizationLMSConfig(
    organizationId: string
  ): Observable<LMSOrganizationConfig | undefined> {
    const db = DBFactory.createDatabase();
    return db
      .streamRecord({
        path: `/lmsOrganizationConfigs/${organizationId}`,
        collection: `/lmsOrganizationConfigs`,
      })
      .pipe(
        map((data) => {
          if (!data) return undefined;

          return LMSOrganizationConfig.fromMap(data);
        })
      );
  }

  static streamOrganizationPurchasedPackages(
    organizationId: string
  ): Observable<OrganizationPackages> {
    const db = DBFactory.createDatabase();
    return db
      .streamList({
        path: `/organizations/${organizationId}/purchasedPackages`,
        collection: `/organizations/${organizationId}/purchasedPackages`,
      })
      .pipe(
        map((data: any) => {
          return data.map((orgPackage: any) => {
            return OrganizationPackage.fromMap(orgPackage);
          });
        })
      );
  }

  static async getOrganizationPurchasedPackages(
    organizationId: string
  ): Promise<OrganizationPackages> {
    let queryConfig: ModelQueryConfig = [];

    const db = DBFactory.createDatabase();
    const data = await db.list(
      {
        path: `/organizations/${organizationId}/purchasedPackages`,
        collection: `/organizations/${organizationId}/purchasedPackages`,
      },
      queryConfig
    );

    return data.map((orgPackage: any) => {
      return OrganizationPackage.fromMap(orgPackage);
    });
  }

  static async getOrganizationPurchasedPackage(
    organizationId: string,
    packageId: string
  ): Promise<OrganizationPackage> {
    const db = DBFactory.createDatabase();
    const data = await db.get({
      path: `/organizations/${organizationId}/purchasedPackages/${packageId}`,
      collection: `/organizations/${organizationId}/purchasedPackages`,
    });

    return OrganizationPackage.fromMap(data);
  }

  static streamOrganizationUsers(
    organizationId: string
  ): Observable<AppUser[]> {
    const db = DBFactory.createDatabase();

    return db
      .streamList(
        {
          path: `/users`,
          collection: `users`,
        },
        [
          {
            type: "where",
            field: "organizationId",
            operator: "==",
            value: organizationId,
          },
          {
            type: "orderBy",
            field: "Email",
            direction: "asc",
          },
        ]
      )
      .pipe(
        map((data: any) => {
          return data.map((user: any) => {
            return user as AppUser;
          });
        })
      );
  }

  static async getOrganizationUsers(organizationId: string) {
    const db = DBFactory.createDatabase();
    const data = await db.list(
      {
        path: `/users`,
        collection: `users`,
      },
      [
        {
          type: "where",
          field: "organizationId",
          operator: "==",
          value: organizationId,
        },
      ]
    );

    return data.map((user: any) => {
      return user as AppUser;
    });
  }

  static streamOrganizationUsedPackages(
    organizationId: string
  ): Observable<UserPackages> {
    const db = DBFactory.createDatabase();
    return db
      .streamList(
        {
          collectionGroup: "packages",
          path: ``,
          collection: "",
        },
        [
          {
            type: "where",
            field: "organizationId",
            operator: "==",
            value: organizationId,
          },
        ]
      )
      .pipe(
        map((data: any) => {
          return data.map((userCap: any) => {
            return UserPackage.fromMap(userCap);
          });
        })
      );
  }

  static async getOrganizationUsedPackages(
    organizationId: string,
    packageId?: string
  ) {
    let queryConfig: ModelQueryConfig = [
      {
        type: "where",
        field: "organizationId",
        operator: "==",
        value: organizationId,
      },
    ];

    if (packageId) {
      queryConfig.push({
        type: "where",
        field: "packageId",
        operator: "==",
        value: packageId,
      });
    }

    const db = DBFactory.createDatabase();

    try {
      const data = await db.list(
        {
          collectionGroup: "packages",
          path: ``,
          collection: "",
        },
        queryConfig
      );
    } catch (error) {}

    const data = await db.list(
      {
        collectionGroup: "packages",
        path: ``,
        collection: "",
      },
      queryConfig
    );

    return data.map((userCap: any) => {
      return UserPackage.fromMap(userCap);
    });
  }

  static async previewSubscriptionImport(
    organizationId: string,
    email: string
  ): Promise<SubscriptionOwnershipTransferPreview> {
    return await $fetch(
      `/api/organizations/${organizationId}/users/subscriptionImportPreview`,
      {
        method: "POST",
        headers: await useApiHeaders(),
        body: { email },
      }
    );
  }

  static async linkUserToOrganization(
    organizationId: string,
    email: string,
    subscriptionDecision: SubscriptionOwnershipDecision = "keep_individual"
  ) {
    return await $fetch(`/api/organizations/${organizationId}/users/linkUser`, {
      method: "POST",
      headers: await useApiHeaders(),
      body: { email, subscriptionDecision },
    });
  }

  static async unlinkUserFromOrganization(
    userId: string,
    organizationId: string,
    userPackages: UserPackages
  ) {
    await $fetch(`/api/organizations/${organizationId}/users/unlinkUser`, {
      method: "POST",
      headers: await useApiHeaders(),
      body: {
        userId,
      },
    });
  }

  static async applyPackageToOrganization({
    capabilityPackage,
    organizationId,
    quantity = 1,
    subscriptionInfo,
  }: {
    capabilityPackage: CapabilityPackage;
    organizationId: string;
    quantity: number;
    subscriptionInfo?: SubscriptionInfo;
  }) {
    const organizationPackage = OrganizationPackage.fromPackage({
      capabilityPackage,
      organizationId,
      quantity,
      subscriptionInfo,
    });

    if (quantity > 0) {
      await organizationPackage.save();
    } else {
      await organizationPackage.delete();
    }
  }

  static async doesUserBelongToOrganization(
    userId: string,
    organizationId: string
  ) {
    try {
      const organizationUsers = await OrganizationsService.getOrganizationUsers(
        organizationId
      );

      const user = organizationUsers.find((user) => user.id === userId);
      return user ? true : false;
    } catch (error) {
      return false;
    }
  }

  static async getNumAvailableLicensesForPackage(
    organizationId: string,
    packageId: string
  ): Promise<number> {
    try {
      const organizationPackage =
        await OrganizationsService.getOrganizationPurchasedPackage(
          organizationId,
          packageId
        );

      // Reduce organizationPackages to a totla quantity
      const totalPurchasedQuantity = organizationPackage.quantity;

      const usedPackages =
        await OrganizationsService.getOrganizationUsedPackages(
          organizationId,
          packageId
        );

      const usedQuantity = usedPackages.reduce(
        (acc, usedPackage) => acc + usedPackage.quantity,
        0
      );

      return totalPurchasedQuantity - usedQuantity;
    } catch (error) {
      return 0;
    }
  }

  static async getOrganizationUserCapabilities({
    organizationId,
    userId,
  }: {
    organizationId: string;
    userId: string;
  }) {
    const response = await $fetch(
      `/api/organizations/${organizationId}/users/${userId}/capabilities`,
      {
        headers: await useApiHeaders(),
      }
    );

    return UserCapabilities.fromMap(response);
  }

  static async applyPackageToUser(
    organizationId: string,
    userId: string,
    packageId: string,
    incrementQuantity: number
  ) {
    const doesUserBelong =
      await OrganizationsService.doesUserBelongToOrganization(
        userId,
        organizationId
      );

    if (doesUserBelong != true) {
      throw new Error("User does not belong to organization");
    }

    const numAvailableLicenses = await this.getNumAvailableLicensesForPackage(
      organizationId,
      packageId
    );

    if (incrementQuantity > 0 && numAvailableLicenses < incrementQuantity) {
      throw new Error("You do not have enough licenses available.");
    }

    const organizationPackage =
      await OrganizationsService.getOrganizationPurchasedPackage(
        organizationId,
        packageId
      );

    const usedPackages = await OrganizationsService.getOrganizationUsedPackages(
      organizationId,
      packageId
    );
    const usedPackagesForUser = usedPackages.filter(
      (usedPackage) => usedPackage.userId === userId
    );

    let existingQuantity = 0;
    if (usedPackagesForUser.length > 0) {
      existingQuantity = usedPackagesForUser[0].quantity;
    }

    // Ensure we can't have a negative amount.
    const newLicenseQuantity = Math.max(
      existingQuantity + incrementQuantity,
      0
    );

    if (organizationPackage.iewActivation) {
      if (newLicenseQuantity === 0) {
        // IEW grant IDs belong to organization purchases, not the package
        // definition catalog. Remove the actual user's grant by its saved ID.
        for (const userPackage of usedPackagesForUser) await userPackage.delete();
        return;
      }
      // IEW activation stays with the original organization admin. Gradebook
      // allocates the purchased seat locally to any member of the organization,
      // preserving the activation snapshot and expiry without updating IEW.
      await UserPackage.applyPackageToUser({
        userId, capabilityPackage: organizationPackage, quantity: newLicenseQuantity,
        organizationId, subscriptionInfo: organizationPackage.subscriptionInfo,
      });
      return;
    }

    await CapabilitiesService.applyPackageToUser({
      packageId,
      userId,
      quantity: newLicenseQuantity,
      organizationId,
      subscriptionInfo: organizationPackage.subscriptionInfo,
    });
  }

  static streamOrganizationSummary({
    organizationId,
  }: {
    organizationId: string;
  }): Observable<OrganizationSummary | undefined> {
    const db = DBFactory.createDatabase();
    return db
      .streamRecord({
        path: `/organizationSummaries/${organizationId}`,
        collection: `/organizationSummaries`,
      })
      .pipe(
        map((data: any) => {
          return data != undefined
            ? OrganizationSummary.fromMap(data)
            : undefined;
        })
      );
  }

  static async getOrganizationSummary({
    organizationId,
  }: {
    organizationId: string;
  }): Promise<OrganizationSummary> {
    const db = DBFactory.createDatabase();
    const data = await db.get({
      path: `/organizationSummaries/${organizationId}`,
      collection: `/organizationSummaries`,
    });

    return OrganizationSummary.fromMap(data);
  }

  static async saveOrganizationSummary({
    organizationId,
  }: {
    organizationId: string;
  }): Promise<void> {
    const organization = await OrganizationsService.get(organizationId);
    const organizationPackages =
      await OrganizationsService.getOrganizationPurchasedPackages(
        organizationId
      );
    const userPackages = await OrganizationsService.getOrganizationUsedPackages(
      organizationId
    );

    const packagesMap = {} as {
      [key: string]: {
        packageId: string;
        packageName: string;
        purchasedQuantity: number;
        usedQuantity: number;
        subscriptionExpiryTimestamp?: number;
      };
    };

    for (const organizationPackage of organizationPackages) {
      const usedPackages = userPackages.filter(
        (userPackage) => userPackage.packageId === organizationPackage.id
      );

      const usedQuantity = usedPackages.reduce(
        (acc, usedPackage) => acc + usedPackage.quantity,
        0
      );

      packagesMap[organizationPackage.id!] = {
        packageId: organizationPackage.id!,
        packageName: organizationPackage.name,
        purchasedQuantity: organizationPackage.quantity,
        usedQuantity,
        subscriptionExpiryTimestamp:
          organizationPackage.subscriptionInfo?.subscriptionExpiryTimestamp,
      };
    }

    const organizationClassrooms =
      await ClassroomsService.getOrganizationClassrooms({
        organizationId,
      });

    const classroomsMap = {} as {
      [key: string]: {
        classroomId: string;
        classroomName: string;
        userId: string;
      };
    };

    const activeOrganizationClassrooms = organizationClassrooms.filter(
      (classroom) => classroom.isArchived != true
    );

    for (const classroom of activeOrganizationClassrooms) {
      classroomsMap[classroom.id!] = {
        classroomId: classroom.id!,
        classroomName: classroom.label ?? classroom.name,
        userId: classroom.userId,
      };
    }
    const organizationSummary = new OrganizationSummary({
      organizationId,
      organizationName: organization.name,
      packagesMap: packagesMap,
      classroomsMap: classroomsMap,
      classroomIds: Object.keys(classroomsMap),
    });

    try {
      await organizationSummary.save();
    } catch (error) {}
  }
}
